import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Cpu, CheckCircle, Linkedin, Award, UserPlus, Download } from "lucide-react";
import { Helmet } from "../components/Helmet";

const OG_IMAGE_API = "https://certificationapi-317500544139.europe-west1.run.app/og-image";
const CERT_PRIMARY = "#7C3BED";
const CERT_PRIMARY_DARK = "#602EC1";
const CERT_PRIMARY_LIGHT = "#EDE9FE";
const CERT_PRIMARY_SOFT = "#F5F3FF";
const CERT_PRIMARY_RGB = "124, 59, 237";
const CERT_GRADIENT = `linear-gradient(135deg, ${CERT_PRIMARY}, ${CERT_PRIMARY_DARK})`;

function ogImageUrl(certId: string | undefined): string {
  return `${OG_IMAGE_API}?id=${encodeURIComponent(certId ?? "")}`;
}


const UPNOV_LINKEDIN_ORG_ID = "114324009";

interface CertData {
  name: string;
  firstName: string;
  company: string;
  courseName: string;
  date: string;
  organisme: string;
}

/** Ornement de coin SVG style diplôme */
function Corner({ flip = false }: { flip?: boolean }) {
  return (
    <svg
      width="48"
      height="48"
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{
        transform: flip ? "scale(-1,1)" : undefined,
        flexShrink: 0,
      }}
    >
      <path d="M4 4 L20 4 L4 20 Z" fill={CERT_PRIMARY} opacity="0.18" />
      <path d="M4 4 L14 4 L4 14 Z" fill={CERT_PRIMARY} opacity="0.35" />
      <path d="M4 4 Q4 22 22 22" stroke={CERT_PRIMARY} strokeWidth="1.2" fill="none" opacity="0.5" />
      <circle cx="4" cy="4" r="2.5" fill={CERT_PRIMARY} opacity="0.6" />
      <path d="M10 4 L10 7 M16 4 L16 6" stroke={CERT_PRIMARY} strokeWidth="1" opacity="0.4" />
      <path d="M4 10 L7 10 M4 16 L6 16" stroke={CERT_PRIMARY} strokeWidth="1" opacity="0.4" />
    </svg>
  );
}

export default function CertificationPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<CertData | null>(null);
  const [loading, setLoading] = useState(true);
  const [emailInput, setEmailInput] = useState("");
  const [emailError, setEmailError] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [savingImage, setSavingImage] = useState(false);
  const currentUrl = typeof window !== "undefined" ? window.location.href : "";

  useEffect(() => {
    if (id) {
      const fetchData = async () => {
        try {
          const res = await fetch(
            `https://certificationapi-317500544139.europe-west1.run.app/?id=${id}`
          );
          if (!res.ok) throw new Error("Network response was not ok");
          const json = await res.json();
          setData(json);
        } catch (error) {
          console.error("Error fetching certification:", error);
        } finally {
          setLoading(false);
        }
      };
      fetchData();
    } else {
      setLoading(false);
    }
  }, [id]);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailLoading(true);
    setEmailError(false);
    try {
      const res = await fetch(
        `https://certificationapi-317500544139.europe-west1.run.app/?email=${encodeURIComponent(emailInput)}`
      );
      if (!res.ok) throw new Error("Not found");
      const json = await res.json();
      if (json.id) {
        navigate(`/certification/${json.id}`);
      } else {
        setData(json);
      }
    } catch {
      setEmailError(true);
    } finally {
      setEmailLoading(false);
    }
  };

  const ogProxyUrl = `https://certificationapi-317500544139.europe-west1.run.app/og-page?id=${encodeURIComponent(id ?? "")}`;
  const linkedinShareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(ogProxyUrl)}`;

  const linkedinAddUrl = (() => {
    if (!data) return "#";
    const [, , issueMonth, issueYear] = data.date.match(/^(\d{2})\/(\d{2})\/(\d{4})$/) ?? [];
    const params = new URLSearchParams({
      startTask: "CERTIFICATION_NAME",
      name: data.courseName,
      organizationId: UPNOV_LINKEDIN_ORG_ID,
      certId: id ?? "",
      certUrl: currentUrl,
      ...(issueYear && issueMonth ? { issueYear, issueMonth } : {}),
    });
    return `https://www.linkedin.com/profile/add/?${params.toString()}`;
  })();

  const handleSaveCertificate = async () => {
    if (!id) return;

    setSavingImage(true);
    try {
      const response = await fetch(ogImageUrl(id));
      if (!response.ok) throw new Error("Image download failed");

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const filename = `certificat-${data?.firstName ?? ""}-${data?.name ?? id}`
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9-]+/g, "-")
        .replace(/^-+|-+$/g, "");

      link.href = objectUrl;
      link.download = `${filename || `certificat-${id}`}.png`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error("Error saving certificate image:", error);
      window.open(ogImageUrl(id), "_blank", "noopener,noreferrer");
    } finally {
      setSavingImage(false);
    }
  };

  /** Balises OG génériques — capturées lors du pré-rendu build-time */
  const helmetGeneric = (
    <Helmet>
      <title>Certificat de Formation — Upnov IA</title>
      <meta property="og:title" content="Certificat de Formation — Upnov IA" />
      <meta property="og:description" content="Certificat de formation en intelligence artificielle délivré par Upnov." />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Upnov IA" />
    </Helmet>
  );

  if (loading) {
    return (
      <>
        {helmetGeneric}
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-primary/10">
          <div className="flex flex-col items-center gap-4">
            <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            <p className="text-gray-500 text-sm">Chargement du certificat…</p>
          </div>
        </div>
      </>
    );
  }

  if (!data) {
    return (
      <>
        {helmetGeneric}
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-primary/10 px-4">
          {!id ? (
            <div className="w-full max-w-sm bg-white rounded-2xl shadow-lg p-8">
              <div className="flex justify-center mb-4">
                <div
                  className="flex items-center justify-center rounded-xl shadow-sm"
                  style={{ background: CERT_GRADIENT, width: 48, height: 48 }}
                >
                  <Award className="h-6 w-6 text-white" />
                </div>
              </div>
              <h2 className="text-center text-lg font-bold text-gray-800 mb-6">Retrouver mon certificat</h2>
              <form onSubmit={handleEmailSubmit} className="flex flex-col gap-4">
                <label className="text-sm font-medium text-gray-700">
                  Entrez votre email :
                </label>
                <input
                  type="email"
                  required
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  placeholder="vous@exemple.com"
                  className="border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
                {emailError && (
                  <p className="text-red-500 text-xs">Aucun certificat trouvé pour cet email.</p>
                )}
                <button
                  type="submit"
                  disabled={emailLoading}
                  className="bg-primary hover:bg-primary/90 text-white font-semibold text-sm rounded-lg py-2 transition-colors disabled:opacity-60"
                >
                  {emailLoading ? "Recherche…" : "Afficher mon certificat"}
                </button>
              </form>
            </div>
          ) : (
            <div className="text-center">
              <Award className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-xl font-semibold text-gray-700">Certificat introuvable</p>
              <p className="text-gray-400 mt-2 text-sm">Vérifiez l'identifiant fourni dans le lien.</p>
            </div>
          )}
        </div>
      </>
    );
  }

  const dynamicTitle = `Certificat ${data.firstName} ${data.name} — ${data.courseName} | Upnov IA`;
  const dynamicDesc  = `${data.firstName} ${data.name} a validé la formation « ${data.courseName} » délivrée par Upnov IA.`;

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center py-8 px-3 sm:px-6"
      style={{ background: `linear-gradient(135deg, ${CERT_PRIMARY_SOFT} 0%, ${CERT_PRIMARY_LIGHT} 100%)` }}
    >
      <Helmet>
        <title>{dynamicTitle}</title>
        <meta property="og:title" content={dynamicTitle} />
        <meta property="og:description" content={dynamicDesc} />
        <meta property="og:image" content={ogImageUrl(id)} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:site_name" content="Upnov IA" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={dynamicTitle} />
        <meta name="twitter:description" content={dynamicDesc} />
        <meta name="twitter:image" content={ogImageUrl(id)} />
      </Helmet>
      {/* Share bar */}
      <div className="w-full max-w-2xl mb-4 flex justify-end gap-2 flex-wrap">
        <button
          type="button"
          onClick={handleSaveCertificate}
          disabled={savingImage || !id}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white shadow-md hover:shadow-lg transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
          style={{ background: CERT_PRIMARY }}
        >
          <Download className="h-4 w-4" />
          {savingImage ? "Enregistrement..." : "Enregistrer"}
        </button>
        <a
          href={linkedinShareUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white shadow-md hover:shadow-lg transition-all duration-200"
          style={{ background: "#0077B5" }}
        >
          <Linkedin className="h-4 w-4" />
          Partager
        </a>
        <a
          href={linkedinAddUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white shadow-md hover:shadow-lg transition-all duration-200"
          style={{ background: "#0077B5" }}
        >
          <UserPlus className="h-4 w-4" />
          Ajouter le certificat à mon profil
        </a>
      </div>

      {/* ── Cadre diplôme extérieur ── */}
      <div
        className="w-full max-w-2xl bg-white shadow-2xl"
        style={{
          border: `6px solid ${CERT_PRIMARY}`,
          borderRadius: "4px",
          padding: "6px",
        }}
      >
        {/* Cadre intérieur fin */}
        <div
          style={{
            border: `1.5px solid rgba(${CERT_PRIMARY_RGB}, 0.35)`,
            borderRadius: "2px",
            padding: "clamp(14px, 4vw, 32px)",
            position: "relative",
          }}
        >
          {/* Ornements de coin */}
          <div style={{ position: "absolute", top: -2, left: -2 }}>
            <Corner />
          </div>
          <div style={{ position: "absolute", top: -2, right: -2, transform: "scale(-1,1)" }}>
            <Corner />
          </div>
          <div style={{ position: "absolute", bottom: -2, left: -2, transform: "scale(1,-1)" }}>
            <Corner />
          </div>
          <div style={{ position: "absolute", bottom: -2, right: -2, transform: "scale(-1,-1)" }}>
            <Corner />
          </div>

          {/* ── Header : logo + badge vérifié ── */}
          <div className="flex items-start justify-between mb-6 sm:mb-8">
            <div className="flex items-center gap-2 sm:gap-3">
              <div
                className="flex items-center justify-center rounded-xl shadow-sm"
                style={{
                  background: CERT_GRADIENT,
                  width: "clamp(36px, 8vw, 48px)",
                  height: "clamp(36px, 8vw, 48px)",
                }}
              >
                <Cpu style={{ width: "clamp(18px, 4vw, 24px)", height: "clamp(18px, 4vw, 24px)" }} className="text-white" />
              </div>
              <div>
                <div
                  className="font-extrabold leading-tight"
                  style={{
                    color: CERT_PRIMARY,
                    fontFamily: "'Poppins', sans-serif",
                    letterSpacing: "0.05em",
                    fontSize: "clamp(14px, 3.5vw, 18px)",
                  }}
                >
                  UPNOV
                </div>
                <div
                  className="text-gray-400 tracking-widest uppercase"
                  style={{ fontSize: "clamp(8px, 2vw, 11px)" }}
                >
                  www.upnov.com
                </div>
              </div>
            </div>

            <div className="flex flex-col items-end gap-1">
              {data?.organisme && (
                <div
                  className="font-semibold text-right"
                  style={{ fontSize: "clamp(18px, 2.5vw, 13px)", color: CERT_PRIMARY }}
                >
                  pour {data.organisme}
                </div>
              )}
              <div
                className="flex items-center gap-1 text-emerald-600 font-semibold"
                style={{ fontSize: "clamp(10px, 2.5vw, 13px)" }}
              >
                <CheckCircle style={{ width: "clamp(12px, 3vw, 16px)", height: "clamp(12px, 3vw, 16px)" }} />
                Certificat vérifié
              </div>
              <div className="text-gray-400 font-mono" style={{ fontSize: "clamp(8px, 2vw, 11px)" }}>
                ID: {id}
              </div>
            </div>
          </div>

          {/* ── Corps du certificat ── */}
          <div className="text-center" style={{ marginBottom: "clamp(16px, 4vw, 32px)" }}>

            <div
              className="font-bold uppercase tracking-[0.3em]"
              style={{ fontSize: "clamp(8px, 2vw, 11px)", marginBottom: "clamp(12px, 3vw, 20px)", color: CERT_PRIMARY }}
            >
              Certificat de Formation
            </div>

            {/* Nom du bénéficiaire */}
            <div
              className="font-extrabold text-gray-900"
              style={{
                fontFamily: "'Poppins', sans-serif",
                fontSize: "clamp(1.4rem, 6vw, 2.5rem)",
                lineHeight: 1.15,
                marginBottom: "clamp(4px, 1.5vw, 10px)",
              }}
            >
              {data.firstName} {data.name}
            </div>



            {/* Séparateur dégradé */}
            <div
              className="mx-auto"
              style={{
                width: "clamp(40px, 10vw, 64px)",
                height: "3px",
                background: `linear-gradient(90deg, ${CERT_PRIMARY}, ${CERT_PRIMARY_DARK})`,
                borderRadius: "9999px",
                marginBottom: "clamp(8px, 2vw, 16px)",
              }}
            />

            <div
              className="text-gray-400"
              style={{ fontSize: "clamp(10px, 2.5vw, 13px)", marginBottom: "clamp(6px, 1.5vw, 10px)" }}
            >
              a validé avec succès la formation
            </div>

            <div
              className="font-bold text-gray-800 leading-snug"
              style={{
                fontFamily: "'Poppins', sans-serif",
                fontSize: "clamp(1rem, 4vw, 1.5rem)",
                marginBottom: "clamp(8px, 2vw, 16px)",
              }}
            >
              {data.courseName}
            </div>

            <div
              className="inline-block text-gray-400 bg-gray-50 rounded-full border border-gray-100"
              style={{
                fontSize: "clamp(9px, 2vw, 12px)",
                padding: "clamp(4px, 1vw, 6px) clamp(10px, 3vw, 16px)",
              }}
            >
              Délivré le {data.date}
            </div>
          </div>

          {/* ── Pied : signature | QR ── */}
          <div className="flex items-end justify-between">
            {/* Signature */}
            <div className="flex flex-col items-start">
              <img
                src="/ccs_signature_certif.png"
                alt="Signature de Cyril Cohen Solal"
                style={{ height: "clamp(36px, 8vw, 56px)", marginBottom: "4px", objectFit: "contain" }}
              />
              <div
                className="font-semibold"
                style={{ fontSize: "clamp(9px, 2.5vw, 13px)", color: CERT_PRIMARY }}
              >
                Cyril Cohen Solal
              </div>
              <div className="text-gray-400" style={{ fontSize: "clamp(8px, 2vw, 11px)" }}>
                Directeur Général — Upnov
              </div>
            </div>

            {/* QR Code */}
            {currentUrl && (
              <div className="flex flex-col items-center gap-1.5">
                <div
                  className="rounded-lg border"
                  style={{ borderColor: `rgba(${CERT_PRIMARY_RGB}, 0.25)`, padding: "clamp(4px, 1vw, 6px)" }}
                >
                  <QRCodeSVG
                    value={currentUrl}
                    size={Math.max(48, Math.min(70, window.innerWidth * 0.13))}
                    level="M"
                    fgColor={CERT_PRIMARY}
                  />
                </div>
                <div
                  className="text-gray-400 text-center leading-tight"
                  style={{ fontSize: "clamp(8px, 1.8vw, 10px)" }}
                >
                  Vérifier en ligne
                </div>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}
